import * as React from 'react'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { cn } from '@/lib/utils'

import { useBranch, type BranchId } from '@/providers/branch-provider'
import { ConversationList } from '@/components/whatsapp/conversation-list'
import { ChatView } from '@/components/whatsapp/chat-view'
import { EmptyState } from '@/components/whatsapp/empty-state'
import { useWAConversationStats } from '@/hooks/useWhatsApp'
import type { WAConversation, WAConversationStatus } from '@/lib/api-client'

// Search params schema
const searchSchema = z.object({
  id: z.coerce.number().optional(),
  status: z.enum(['open', 'archived', 'closed', 'all']).optional(),
})

export const Route = createFileRoute('/whatsapp')({
  component: WhatsAppPage,
  validateSearch: searchSchema,
})

// Branch code to ID mapping
const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function WhatsAppPage() {
  const { branchId } = useBranch()
  const search = useSearch({ from: '/whatsapp' })

  // Stats for header
  const { data: stats } = useWAConversationStats()

  // State
  const [selectedConversation, setSelectedConversation] = React.useState<WAConversation | null>(null)
  const [statusFilter, setStatusFilter] = React.useState<WAConversationStatus | 'all'>(
    search.status || 'all'
  )
  const [searchQuery, setSearchQuery] = React.useState('')

  // Mobile view state
  const [showChat, setShowChat] = React.useState(false)

  // Handle conversation selection
  const handleSelectConversation = React.useCallback((conversation: WAConversation) => {
    setSelectedConversation(conversation)
    setShowChat(true) // Show chat panel on mobile
  }, [])

  // Handle back button on mobile
  const handleBackToList = React.useCallback(() => {
    setShowChat(false)
  }, [])

  // Sync with URL search param
  React.useEffect(() => {
    if (search.status) {
      setStatusFilter(search.status)
    }
  }, [search.status])

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-page">
      {/* Content - Full height, no page header */}
      <div className="flex-1 flex flex-col min-h-0 p-3 sm:p-4 lg:p-6">
        {/* Split Panel Card */}
        <div className="flex flex-1 min-h-0 overflow-hidden rounded-xl border border-[#e9edef] bg-white shadow-sm">
          {/* Conversation List Panel */}
          <div
            className={cn(
              'flex flex-col border-r border-[#e9edef] transition-all duration-300',
              // Mobile: full width when showing list, hidden when showing chat
              showChat ? 'hidden lg:flex' : 'flex w-full',
              // Desktop: fixed width
              'lg:w-[380px] lg:min-w-[380px]'
            )}
          >
            <ConversationList
              branchId={BRANCH_IDS[branchId]}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
              stats={stats}
            />
          </div>

          {/* Chat View Panel */}
          <div
            className={cn(
              'flex-1 flex flex-col min-h-0 min-w-0 transition-all duration-300',
              // Mobile: full width when showing chat, hidden when showing list
              !showChat ? 'hidden lg:flex' : 'flex'
            )}
          >
            {selectedConversation ? (
              <ChatView
                conversation={selectedConversation}
                onBack={handleBackToList}
              />
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
